import Control from '@/Controls/Control';
import Utilities from '@/Utility/Utilities';
import ImageViewModel from '@/ViewModels/Controls/ImageViewModel';
import Image from '@/Controls/Image';
import GlobalConfiguration from '@/Management/GlobalConfiguration';
import {DroppedElements, DraggedElements } from '#/CoreFrameInterfaces';

export default class EventHelper {

    private static CLASS_DROPOVERLAY = 'dropoverlay' as const;
    private static IGNORED_BY_RAD_KEY = "ignoreByRAD" as const;

    /**
     * Bestimmt, ob ein Klick auf ein Element in einem neuen Fenster geöffnet werden soll
     * @param event
     */
    public static shouldOpenClickInNewWindow(event: JQuery.Event | MouseEvent | TouchEvent): boolean {
        // TouchEvents besitzen keinen Maus-Button; nur Maus-Klicks können in einem neuen Fenster geöffnet werden
        if (!('button' in event))
            return false;
        return (event.ctrlKey && event.button === 0) || event.button === 1;
    }

    /**
     * Bindet ein Klick-Event an ein Element (Mausrad-Klick ist eingeschlossen)
     * @param element
     * @param callback
     */
    public static bindClickEvent(element: JQuery<HTMLElement> | HTMLElement, callback: (openInNewWindow: boolean, ev: JQuery.MouseUpEvent | JQuery.TouchEndEvent) => void, control?: Control): void {
        let mousedownTarget = null;
        const preventDefault = (event: JQuery.UIEventBase) => {
            mousedownTarget = event.target;
            event.preventDefault();
        };
        const eventCallback = (event: JQuery.MouseUpEvent | JQuery.TouchEndEvent) => {
            const currentTarget = event.currentTarget as HTMLElement;

            // CF-92804 Klick wird bei Loslassen auf anderem Element falsch gefeuert
            // CF-93096 Zurueck-Button beim 2FA Token Dialog funktioniert manchmal nicht
            // Nur wenn das MouseDown-Element im currentTarget ist, für das das Event registriert wurde
            if (!currentTarget.contains(mousedownTarget) && event.button === 0)
                return;
            if (event.button === 2)
                return;

            const openInNewWindow = EventHelper.shouldOpenClickInNewWindow(event);
            callback(openInNewWindow, event);
        };

        
        if (Utilities.isNullOrUndefined(control)) {
            const $element = element instanceof HTMLElement ? $(element) : element;
            $element.on('mousedown', preventDefault);
            $element.on('mouseup', eventCallback);
            $element.on('touchstart', preventDefault);
            $element.on('touchend', eventCallback);
        } else {
            const jQueryElement = element instanceof HTMLElement ? $(element) : element;
            control.addEventHandler('mousedown', jQueryElement, preventDefault);
            control.addEventHandler('mouseup', jQueryElement, eventCallback);
            control.addEventHandler('touchstart', jQueryElement, preventDefault);
            control.addEventHandler('touchend', jQueryElement, eventCallback);
        }
    }

    /**
     * Bindet ein Drop-Event an ein Element
     * @param element
     * @param isDropAllowed
     * @param onNotAllowed
     * @param checkDroppedElementAllowed
     * @param onElementsAllowed
     * @param onElementsNotAllowed
     * @param onDropped
     */
    public static bindDropEvent(element: JQuery<HTMLElement>, isDropAllowed: () => boolean, onNotAllowed: () => void, checkDroppedElementAllowed: (draggedElements: DraggedElements) => boolean, onElementsAllowed: () => void, onElementsNotAllowed: () => void, onDropped: (droppedElements: DroppedElements) => void): void {
        if (Utilities.isNullOrUndefined(element))
            return;

        const overlay = $(`<div class="${EventHelper.CLASS_DROPOVERLAY}">`);
        
        overlay.prependTo(element);

        let isDropEnabled: boolean;

        element.on('dragenter', (event: JQuery.DragEnterEvent) => {
            EventHelper.preventDefault(event);
            const ev = event.originalEvent;

            overlay.show();
            if (!isDropAllowed()) {
                onNotAllowed();
                ev.dataTransfer.effectAllowed = 'none';
                ev.dataTransfer.dropEffect = 'none';
                return;
            }

            const draggedElements = EventHelper.getDraggedElements(ev);
            if (checkDroppedElementAllowed(draggedElements)) {
                if (Utilities.isFunction(onElementsAllowed))
                    onElementsAllowed();
                ev.dataTransfer.effectAllowed = 'copy';
                ev.dataTransfer.dropEffect = 'copy';
                isDropEnabled = true;
            } else {
                if (Utilities.isFunction(onElementsNotAllowed))
                    onElementsNotAllowed();
                ev.dataTransfer.effectAllowed = 'none';
                ev.dataTransfer.dropEffect = 'none';
                isDropEnabled = false;
            }
        });

        overlay.on('dragover', (event: JQuery.DragOverEvent) => {
            // Damit das drop funktioniert, müssen alle anderen Events abgebrochen werden
            // s. https://stackoverflow.com/questions/19223352/jquery-ondrop-not-firing
            EventHelper.preventDefault(event);

            const ev = event.originalEvent;
            if(isDropEnabled && isDropAllowed())
                ev.dataTransfer.dropEffect = 'copy';
            else
                ev.dataTransfer.dropEffect = 'none';
        });

        overlay.on('dragleave', (event: JQuery.DragLeaveEvent) => {
            EventHelper.preventDefault(event);
            
            overlay.hide();
        });

        overlay.on('drop', (event: JQuery.DropEvent) => {
            EventHelper.preventDefault(event);

            if (!isDropAllowed()) {
                overlay.hide();
                return;
            }

            // !!! Drop setzt nicht den Fokus
            // Da der Loader das ursprünglich aktive Element nach dem Ausblenden des Loaders wiederherstellt,
            // muss das Element, welches das Drop-Element empfängt als als aktives Element gesetzt werden, daher:
            Utilities.focusFirstFocusableElement(element);

            try {
                const ev = event.originalEvent as DragEvent;
                const droppedElements = EventHelper.getDroppedElements(ev);
                onDropped(droppedElements);
            }
            finally {
                overlay.hide();
            }
        });
    }

    /**
     * Bindet ein Drop-Event an ein Element und signalisiert beim Drag-Over,
     * dass das Droppen an dieser Stelle nicht möglich ist
     * @param element
     */
    public static bindDropNotPossible(element: JQuery<HTMLElement>) {
        this.bindDropAndAddEvent(element, () => false, null, null, null, null);
    }

    /**
     * Bindet ein Drop-Event an ein Element, welches das Standardverhalten für das Hochladen
     * von Dateien und Texten bereits zu Verfügung stellt
     * @param element
     * @param isEnabled
     * @param elementsAreAllowedToAdd
     * @param onAllowed
     * @param onNotAllowed
     * @param onAdd
     */
    public static bindDropAndAddEvent(element: JQuery<HTMLElement>, isEnabled: () => boolean, elementsAreAllowedToAdd: (draggedElements: DraggedElements) => boolean, onAllowed: () => void, onNotAllowed: () => void, onAdd: (droppedElements: DroppedElements) => void): void {
        let imageAllowed: JQuery<HTMLElement> = null;
        let imageNotAllowed: JQuery<HTMLElement> = null;

        EventHelper.bindDropEvent(element, isEnabled,
            () => {
                imageAllowed.hide();
                imageNotAllowed.show();
            },
            elementsAreAllowedToAdd, () => {

                imageNotAllowed.hide();
                imageAllowed.show();
                if (Utilities.isFunction(onAllowed))
                    onAllowed();

            }, () => {
                imageAllowed.hide();
                imageNotAllowed.show();
                if (Utilities.isFunction(onNotAllowed))
                    onNotAllowed();
            }, onAdd);

        const overlay = element.find(`.${EventHelper.CLASS_DROPOVERLAY}`);
        imageAllowed = EventHelper.createImageForOverlay(overlay, GlobalConfiguration.inlineImages.MEDIUM_UPLOAD, 'drop-allowed');
        imageNotAllowed = EventHelper.createImageForOverlay(overlay, GlobalConfiguration.inlineImages.MEDIUM_UPLOAD_NOTALLOWED, 'drop-not-allowed');
    }

    /**
     * Ermittelt Informationen über die gedroppten Elemente
     * @param event
     */
    public static getDroppedElements(event: DragEvent): DroppedElements {
        const droppedElements: DroppedElements = { links: [], files: [] };

        _.each(event.dataTransfer.files, (file) => { droppedElements.files.push(file); });

        const link = EventHelper.getLink(event);
        if (link)
            droppedElements.links = [link];
               
        return droppedElements;
    }

    /**
     * Ermittelt Informationen über die gedraggten Elemente
     * Diese können bspw. im IE sehr spärlich ausfallen
     * @param event
     */
    public static getDraggedElements(event: DragEvent): DraggedElements {
        const draggedElements: DraggedElements = {
            fileCount: 0,
            fileTypes: [],
            isText: false,
            isFile: false,
            fileCountNotValidated: false
        };

        const isFile = EventHelper.checkDragEventForFile(event);

        if (isFile) {
            draggedElements.isFile = true;
            if (!Utilities.isIE()) {
                for (let x = 0; x < event.dataTransfer.items.length; x++) {
                    const currentDataTransferItem = event.dataTransfer.items[x];
                    if (currentDataTransferItem.kind === 'file') {
                        draggedElements.fileCount++;
                        draggedElements.fileTypes.push(currentDataTransferItem.type);
                    }
                }
            } else {
                draggedElements.fileCountNotValidated = true;
            }
        } else if (event.dataTransfer.types.length > 0) {
            draggedElements.isText = true;
        }

        return draggedElements;
    }

    /**
     * Gibt zurück, ob im DragEvent Dateien gedraggt wurden
     * @param event
     */
    private static checkDragEventForFile(event: DragEvent): boolean {
        // Im IE ist event.dataTransfer.types eine DOMStringList
        // s. https://developer.mozilla.org/en-US/docs/Web/API/DataTransfer/types
        if (event.dataTransfer.types instanceof DOMStringList) {
            return event.dataTransfer.types.contains('Files');
        } else {
            // Andernfalls ist ein String-Array
            return event.dataTransfer.types.indexOf('Files') > -1;
        }
    }


    /**
     * Validiert gedraggte Elemente anhand der eingehenden Prüfkriterien
     * Diese Methode berücksichtigt browserspezifische "Probleme" und gibt im Fall von Unsicherheiten true zurück
     * @param draggedElements
     * @param onlyFile
     * @param onlyText
     * @param maxFiles
     */
    public static validateDraggedElements(draggedElements: DraggedElements, onlyFile: boolean, onlyText: boolean, maxFiles?: number): boolean {
        // Keine Datei, wenn es nur eine Datei sein darf
        if (onlyFile && (!draggedElements.isFile || draggedElements.isText))
            return false;

        // Keine Text, wenn es nur Text sein darf
        if (onlyText && (!draggedElements.isText || draggedElements.isFile))
            return false;

        // Wenn maxFiles gesetzt, dann muss dies validiert werden
        if (!Utilities.isNullOrUndefined(maxFiles)) {
            // FileSize kann nicht genau ermittelt werden, daher immer true
            if (draggedElements.fileCountNotValidated)
                return true;

            if (draggedElements.fileCount > maxFiles)
                return false;
        }

        return true;
    }

    /**
     *  Extrahiert einen Link aus einem DragEvent
     * @param event
     */
    private static getLink(event: DragEvent): string {
        // Da der IE getData("text/uri-list") nicht unterstützt müssen wir selber prüfen, ob es sich um einen Link handelt
        if (Utilities.isIE()) {
            const text = event.dataTransfer.getData('text');
            if (Utilities.isUrl(text))
               return text;
        }
        else {
            // Für Links die in den FireFox gezogen werden
            let link = event.dataTransfer.getData('text/x-moz-url');
            if (link) {
                // Dieses Format liefert ebenfalls den Link Namen mit, welcher nach einem Zeilenumbruch steht
                const index = link.indexOf('\n');
                if (index > 0)
                    return link.substring(0, index);
            }
            else {
                link = event.dataTransfer.getData('text/uri-list');
                if (link)
                    return link;

                const plain = event.dataTransfer.getData('text/plain');
                if (Utilities.isUrl(plain))
                    return plain;
            }
        }

        return '';
    }

    /**
     * Erstellt ein Plus-Image, welches mittig im Overlay angezeigt wird.
     * @param overlay
     */
    private static createImageForOverlay(overlay: JQuery<HTMLElement>, source: string, cssClass: string): JQuery<HTMLElement> {
        const imageVM = new ImageViewModel({
            source: source
        });
        const image = new Image(imageVM, $('<div>')).getControl();
        image.addClass(cssClass);
        image.hide();
        image.appendTo(overlay);

        return image;
    }

    /**
     * Verhindert das Standardverhalten.
     * @param event
     */
    private static preventDefault(event: any): void {
        event.preventDefault();     
        event.stopPropagation();
    }

    public static ignoreRADEventHandlers(element: HTMLElement) {
        element.dataset[this.IGNORED_BY_RAD_KEY] = "1";
    }

    public static isIgnoredByRADEventHandlers(element: HTMLElement): boolean {
        return element.dataset[this.IGNORED_BY_RAD_KEY] === "1" || $(element).data(this.IGNORED_BY_RAD_KEY) === true;
    }
}